import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Card, Chip, Collapse, Divider, ListItemButton, ListItemText, Stack, Typography } from '@mui/material'
import { FieldArrayPathByValue } from '@src/services/react-hook-form'
import { useState } from 'react'
import { Control, FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import DeleteIconButton from '../Common/Button/DeleteIconButton'
import EditIconButton from '../Common/Button/EditIconButton'
import type { DisbursementLineSchemaType, DisbursementSchemaType } from './loan-form-schema'
import DisbursementDialog from './DisbursementDialog'
import DisbursementLineList from './DisbursementLineList'

interface Props<TFieldValues extends FieldValues> {
  disbursement: DisbursementSchemaType
  control: Control<TFieldValues>
  name: FieldArrayPathByValue<TFieldValues, DisbursementLineSchemaType>
  onDelete: () => void
  onUpdate: (data: DisbursementSchemaType) => void
}

const DisbursementItem = <TFieldValues extends FieldValues = FieldValues>({
  disbursement,
  control,
  name,
  onDelete,
  onUpdate,
}: Props<TFieldValues>) => {
  const { t } = useTranslation()
  const [openCollapse, setOpenCollapse] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)

  const onProductLineAdded = (data: DisbursementLineSchemaType) => {
    disbursement.lines.push(data)
  }

  const onProductLineUpdated = (data: DisbursementLineSchemaType, index: number) => {
    disbursement.lines[index] = data
  }

  const onProductLineRemoved = (index: number) => {
    disbursement.lines = disbursement.lines.filter((_, id) => id !== index)
  }

  return (
    <Card variant="outlined">
      <ListItemButton onClick={() => setOpenCollapse((o) => !o)}>
        <ListItemText
          primary={
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography>{disbursement.payeeId}</Typography>
                <Chip label={t(`enum.targetType.${disbursement.payeeType}`)} />
                <Chip label={t(`enum.EPaymentMethod.${disbursement.paymentMethod}`)} />
              </Stack>
              <div>
                <EditIconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    setOpenDialog(true)
                  }}
                />
                <DeleteIconButton
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete()
                  }}
                />
              </div>
            </Stack>
          }
        />
        {openCollapse ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </ListItemButton>
      <Collapse in={openCollapse} timeout="auto" unmountOnExit>
        <Divider />
        <DisbursementLineList
          control={control}
          name={name}
          onInsert={onProductLineAdded}
          onDelete={onProductLineRemoved}
          onUpdate={onProductLineUpdated}
        />
      </Collapse>
      <DisbursementDialog
        open={openDialog}
        payeeType={disbursement.payeeType}
        onClose={() => setOpenDialog(false)}
        onSubmit={(data) => onUpdate(data as never)}
        defaultValues={disbursement}
      />
    </Card>
  )
}

export default DisbursementItem
